import {
  isCombinedSimpleModel,
  isDateFilter,
  isMultiFilter,
  isNumberFilter,
  isSetFilter,
  isTextFilter,
} from 'components/Blotters/Filter/Badges/type-guards';
import type { ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { first } from 'lodash-es';
import type { MakePartialNullable } from 'types/utils';

export type ExtractedFilter = {
  type: 'date' | 'text' | 'number' | 'set';
  value: string;
  boolOperator?: BoolOperator;
  comparisonOperator?: string | null | ComparisonOperator;
};
type BoolOperator = 'AND' | 'OR';

type ComparisonOperator =
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'
  | 'startsWith'
  | 'endsWith'
  | 'blank'
  | 'notBlank'
  | 'in'
  | 'empty';

export function extractSearchedValueFromComplexFilterModel(filterModel: any): string | undefined {
  return first(getFilterValues({ filter: filterModel?.filter, filterModel: filterModel }))?.value;
}

export const getFilterValues = (col?: Pick<ColumnPreferenceState, 'filterModel' | 'filter'>): ExtractedFilter[] => {
  const values = extractFilterFromModel(col?.filterModel);

  if (values.length === 0 && !!col?.filter) {
    return [{ type: 'text', value: col.filter }];
  }
  return values;
};

function extractFilterFromModel(model?: any, boolOperator?: BoolOperator): ExtractedFilter[] {
  type NotReadyExtractedFilter = MakePartialNullable<ExtractedFilter, 'value'>;

  let values: NotReadyExtractedFilter[] = [];
  if (isMultiFilter(model)) {
    values = (model.filterModels ?? []).flatMap(m => extractFilterFromModel(m));
  } else if (isCombinedSimpleModel(model)) {
    values = [...values, ...extractFilterFromModel(model.conditions?.[0], model.operator)];
    values = [...values, ...extractFilterFromModel(model.conditions?.[1])];
  } else if (isTextFilter(model)) {
    values = [{ value: model.filter ?? '', comparisonOperator: model.type, boolOperator, type: 'text' }];
  } else if (isSetFilter(model)) {
    if (model.values.length === 0) {
      values = [{ type: 'text', value: 'empty', comparisonOperator: 'in' }];
    } else {
      values = (model.values ?? []).map(v => ({
        type: 'text',
        value: v === null ? 'blanks' : v,
        comparisonOperator: 'in',
      }));
    }
  } else if (isDateFilter(model)) {
    values = [{ type: 'date', value: model.dateFrom, comparisonOperator: model.type, boolOperator }];
  } else if (isNumberFilter(model)) {
    values = [{ type: 'number', value: model.filter?.toString(), comparisonOperator: model.type, boolOperator }];
  }
  return values as ExtractedFilter[];
}
